// src/features/maintenance/MaintenancePanel.jsx — the vehicle's plans, last-serviced edit, Mark complete, history (spec §8.3).
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Wrench, Loader2, CheckSquare } from 'lucide-react';
import { maintenancePlans } from '@/lib/trackizy';
import MarkCompleteDialog from './MarkCompleteDialog';
import { remainingText, statusHint, statusLabel } from './statusLabels';

const BADGE = { overdue: 'text-red-600 bg-red-50', dueSoon: 'text-amber-600 bg-amber-50', ok: 'text-emerald-600 bg-emerald-50', noReading: 'text-slate-500 bg-slate-100' };
const input = 'h-8 px-2 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-slate-200 outline-none';

function BaselineEditor({ row, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [date, setDate] = useState(row.since.at.slice(0, 10));
  const [km, setKm] = useState(row.since.km != null ? String(row.since.km) : '');
  const [hours, setHours] = useState(row.since.hours != null ? String(row.since.hours) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const src = (k) => (k === 'vehicle' ? t('maintenancePlans.sourceVehicle', 'from the vehicle') : t('maintenancePlans.sourceTracker', 'from the tracker'));
  const save = async () => {
    setSaving(true); setError(null);
    try {
      await maintenancePlans.setBaseline(row.planId, row.deviceId, { since_at: new Date(`${date}T00:00:00.000Z`).toISOString(), since_km: km !== '' ? Number(km) : null, since_hours: hours !== '' ? Number(hours) : null });
      onSaved();
    } catch (err) { setError(err?.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 text-xs">
      <label className="flex flex-col gap-0.5 text-[10px] text-slate-500">{t('maintenancePlans.lastServicedDate', 'Date')}<input data-testid="baseline-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} /></label>
      <label className="flex flex-col gap-0.5 text-[10px] text-slate-500">{t('maintenancePlans.odometerKm', 'Odometer (km)')}<input data-testid="baseline-km" type="number" min="0" step="any" value={km} onChange={(e) => setKm(e.target.value)} className={`${input} w-28`} /></label>
      <label className="flex flex-col gap-0.5 text-[10px] text-slate-500">{t('maintenancePlans.engineHours', 'Engine hours')}<input data-testid="baseline-hours" type="number" min="0" step="any" value={hours} onChange={(e) => setHours(e.target.value)} className={`${input} w-24`} /></label>
      <button data-testid="baseline-save" type="button" disabled={saving} onClick={save} className="h-8 px-3 rounded-lg text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-[11px] font-semibold">{t('common.save', 'Save')}</button>
      <button type="button" onClick={onCancel} className="h-8 px-3 rounded-lg text-slate-600 bg-slate-100 dark:bg-slate-800 text-[11px]">{t('common.cancel', 'Cancel')}</button>
      {error && <span className="text-red-600">{error}</span>}
      {(row.reading?.km != null || row.reading?.hours != null) && (
        <p data-testid="baseline-source" className="basis-full text-[11px] text-slate-400 dark:text-slate-500">
          {[row.reading.km != null && <span key="km">{t('maintenancePlans.odometerKm', 'Odometer (km)')}: <bdi>{row.reading.km.toLocaleString()}</bdi> {src(row.reading.kmSource)}</span>,
            row.reading.hours != null && <span key="h">{t('maintenancePlans.engineHours', 'Engine hours')}: <bdi>{row.reading.hours.toLocaleString()}</bdi> {src(row.reading.hoursSource)}</span>]
            .filter(Boolean).reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}
        </p>
      )}
    </div>
  );
}

export default function MaintenancePanel({ deviceId, canEdit }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // planId
  const [completing, setCompleting] = useState(null); // row

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([maintenancePlans.status({ deviceId }).catch(() => []), maintenancePlans.events({ device_id: deviceId }).catch(() => [])]);
    setRows(Array.isArray(s) ? s : []);
    setEvents((Array.isArray(e) ? e : []).slice(0, 10));
  }, [deviceId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const since = (row) => {
    const parts = [row.since.at.slice(0, 10)];
    if (row.since.km != null) parts.push(`${row.since.km.toLocaleString()} km`);
    if (row.since.hours != null) parts.push(`${row.since.hours.toLocaleString()} h`);
    return parts.join(' · ');
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {completing && <MarkCompleteDialog row={completing} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); load(); }} />}
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
        <span className="flex items-center gap-2"><Wrench size={14} className="text-slate-400" />{t('maintenancePlans.panelTitle', 'Maintenance')}{rows && <span className="text-slate-400 font-normal">({rows.length})</span>}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {rows === null ? <Loader2 size={16} className="animate-spin text-slate-400" /> : rows.length === 0 ? (
            <p className="text-xs text-slate-400">{t('maintenancePlans.noPlansForVehicle', 'No maintenance plans apply to this vehicle.')}</p>
          ) : rows.map((row) => (
            <div key={row.planId} className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{row.planName}</p>
                  <p className="text-[11px] text-slate-500"><bdi>{remainingText(row, t)}</bdi></p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${BADGE[row.status] || BADGE.noReading}`} title={statusHint(row, t)}>{statusLabel(row.status, t)}</span>
              </div>
              <p data-testid={`since-${row.planId}`} className="text-[11px] text-slate-400 mt-1">{t('maintenancePlans.lastServiced', 'Last serviced')}: <bdi>{since(row)}</bdi></p>
              {canEdit && editing !== row.planId && (
                <div className="flex gap-3 mt-2">
                  <button type="button" onClick={() => setCompleting(row)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline"><CheckSquare size={11} />{t('maintenance.markComplete', 'Mark complete')}</button>
                  <button type="button" onClick={() => setEditing(row.planId)} className="text-[11px] font-semibold text-blue-600 hover:underline">{t('maintenancePlans.setLastServiced', 'Set last serviced')}</button>
                </div>
              )}
              {editing === row.planId && <BaselineEditor row={row} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
            </div>
          ))}
          {events.length > 0 && (
            <div data-testid="history">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{t('maintenance.history', 'History')}</p>
              <table className="w-full text-[11px]">
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1 text-slate-500">{String(ev.completed_at).slice(0, 10)}</td>
                      <td className="py-1 text-slate-700 dark:text-slate-300">{ev.name}</td>
                      <td className="py-1 text-slate-500 tabular-nums text-end">{ev.odometer_km != null ? `${ev.odometer_km.toLocaleString()} km` : ''}</td>
                      <td className="py-1 text-slate-500 tabular-nums text-end">{ev.cost != null ? ev.cost.toLocaleString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
